import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Flight, FlightStatus } from "@/types/flight";
import { Search, LogOut, Mic, Loader2, MousePointerClick, Check, ChevronRight, ChevronDown } from "lucide-react";
import type { Session } from "@supabase/supabase-js";

// ── Constants ──────────────────────────────────────────────
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const WARNING_BEFORE = 60 * 1000; // 60 seconds

const STATUS_BUTTONS: { label: FlightStatus; bg: string; text: string }[] = [
  { label: "On Time", bg: "#FFDE21", text: "#0a0f2c" },
  { label: "Check-in Open", bg: "#2563EB", text: "#ffffff" },
  { label: "Boarding Soon", bg: "#F59E0B", text: "#ffffff" },
  { label: "Boarding", bg: "#16A34A", text: "#ffffff" },
  { label: "Gate Closed", bg: "#991B1B", text: "#ffffff" },
  { label: "Delayed", bg: "#DC2626", text: "#ffffff" },
  { label: "Cancelled", bg: "#B91C1C", text: "#ffffff" },
  { label: "Departed", bg: "#16A34A", text: "#ffffff" },
  { label: "In Air", bg: "#16A34A", text: "#ffffff" },
  { label: "Landed", bg: "#16A34A", text: "#ffffff" },
  { label: "Arrived", bg: "#16A34A", text: "#ffffff" },
];

const ALLOWED_ROLES = ["admin", "administrator", "organization", "organization_user"];

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", {
    timeZone: "Pacific/Port_Moresby",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// ── Live Clock ─────────────────────────────────────────────
function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const png = new Date(time.toLocaleString("en-US", { timeZone: "Pacific/Port_Moresby" }));
  const hh = png.getHours().toString().padStart(2, "0");
  const mm = png.getMinutes().toString().padStart(2, "0");
  const dd = png.getDate().toString().padStart(2, "0");
  const mo = (png.getMonth() + 1).toString().padStart(2, "0");
  const yr = png.getFullYear();

  return (
    <div className="text-right font-mono">
      <div className="text-white font-bold text-2xl md:text-3xl leading-none tracking-wider">
        {hh}:{mm}
      </div>
      <div className="text-white/80 text-xs leading-tight">
        {dd}-{mo}-{yr}
      </div>
    </div>
  );
}

// ── Login Screen ───────────────────────────────────────────
function FastUpdateLogin({
  onLogin,
}: {
  onLogin: (session: Session, profile: { role: string; organization_id: string | null }) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw authError;
      if (!data.session) throw new Error("No session returned");

      // Fetch profile to check role
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("role, organization_id")
        .eq("id", data.session.user.id)
        .single();

      if (profileError || !profile) {
        throw new Error("Unable to load user profile.");
      }

      if (!ALLOWED_ROLES.includes(profile.role)) {
        setError("You do not have permission to use this tool.");
        return;
      }

      onLogin(data.session, profile as { role: string; organization_id: string | null });
    } catch (err: any) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0f2c" }}>
      <div
        className="w-full max-w-sm rounded-xl p-8"
        style={{ background: "#0d1b4b", border: "1px solid #1e3a6e" }}
      >
        <div className="flex justify-center mb-6">
          <img src="/NAC_logo.png" alt="NAC Logo" className="h-14 w-auto" />
        </div>
        <h1 className="text-center text-white font-bold text-xl mb-6">Flight Update Tool</h1>

        {error && (
          <div className="mb-4 rounded-lg px-4 py-3 text-sm font-medium text-red-300 bg-red-900/40 border border-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg text-white border-0 outline-none placeholder:text-white/40"
            style={{ background: "#0a0f2c", caretColor: "#FFD700" }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-lg text-white border-0 outline-none placeholder:text-white/40"
            style={{ background: "#0a0f2c", caretColor: "#FFD700" }}
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg text-lg font-bold transition-all disabled:opacity-50"
            style={{ background: "#FFDE21", color: "#0a0f2c" }}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────
export default function FastUpdate() {
  // Auth state (isolated)
  const [session, setSession] = useState<Session | null>(null);
  const [storedTokens, setStoredTokens] = useState<{ access_token: string; refresh_token: string } | null>(null);
  const [profile, setProfile] = useState<{ role: string; organization_id: string | null } | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Inactivity
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const warningRef = useRef<ReturnType<typeof setTimeout>>();

  // Search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Flight[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<FlightStatus | null>(null);
  const [statusReason, setStatusReason] = useState("");
  const [updating, setUpdating] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ number: string; status: string } | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Initialization error
  const [initError, setInitError] = useState<string | null>(null);

  // Mode toggle: "text" or "voice"
  const [mode, setMode] = useState<"text" | "voice">("text");

  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [transcriptError, setTranscriptError] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // ── Ensure session is set on shared client before writes ──
  const ensureSession = useCallback(async () => {
    if (storedTokens) {
      await supabase.auth.setSession({
        access_token: storedTokens.access_token,
        refresh_token: storedTokens.refresh_token,
      });
    }
  }, [storedTokens]);

  // ── Logout helper ──────────────────────────────────────
  const logout = useCallback(async () => {
    setSession(null);
    setProfile(null);
    setStoredTokens(null);
    setShowWarning(false);
    // Only sign out if no other main app session exists
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        // There's a session — only sign out if it matches our fast-update session
        // Don't sign out if it's a different user's session from the main app
      }
    } catch {
      // ignore
    }
  }, []);

  // ── Inactivity timer ───────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    setShowWarning(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);

    timeoutRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  // Start/stop inactivity tracking when session changes
  useEffect(() => {
    if (!session) return;
    resetInactivityTimer();

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => resetInactivityTimer();
    events.forEach((e) => window.addEventListener(e, handler));

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [session, resetInactivityTimer]);

  // ── Check existing session on mount ───────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data: { session: s }, error: sessErr } = await supabase.auth.getSession();
        if (sessErr) throw sessErr;
        if (s) {
          // Explicitly set session on the shared client first
          await supabase.auth.setSession({
            access_token: s.access_token,
            refresh_token: s.refresh_token,
          });
          const { data: p, error: profErr } = await supabase
            .from("profiles")
            .select("role, organization_id")
            .eq("id", s.user.id)
            .single();
          if (profErr) throw profErr;
          if (p && ALLOWED_ROLES.includes(p.role)) {
            setSession(s);
            setStoredTokens({
              access_token: s.access_token,
              refresh_token: s.refresh_token,
            });
            setProfile(p as { role: string; organization_id: string | null });
          }
        }
      } catch (err: any) {
        console.error("FastUpdate init error:", err);
        setInitError(`⚠️ Failed to load Flight Update Tool: ${err.message || "Unknown error"}`);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, []);

  // ── Click outside to close dropdown ────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isOrgUser = profile?.role === "organization" || profile?.role === "organization_user";

  // ── Search flights ─────────────────────────────────────
  const searchFlights = useCallback(
    async (q: string) => {
      if (q.length < 1) {
        setResults([]);
        setShowDropdown(false);
        setSearchError(null);
        return;
      }
      setSearching(true);
      setSearchError(null);
      try {
        // Ensure session is set before querying
        await ensureSession();

        const now = new Date();
        const pngDate = new Date(now.toLocaleString("en-US", { timeZone: "Pacific/Port_Moresby" }));
        const startOfDay = new Date(pngDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(pngDate);
        endOfDay.setHours(23, 59, 59, 999);

        console.log("[FastUpdate] Searching flights:", q, "date range:", startOfDay.toISOString(), "to", endOfDay.toISOString());

        let qb = supabase
          .from("unified_flights" as any)
          .select("*")
          .gte("dep_datetime", startOfDay.toISOString())
          .lt("dep_datetime", endOfDay.toISOString())
          .ilike("flight_number", `%${q}%`)
          .order("dep_datetime", { ascending: true })
          .limit(15);

        if (isOrgUser && profile?.organization_id) {
          qb = qb.eq("organization_id", String(profile.organization_id));
        }

        const { data, error } = await qb;
        if (error) throw error;

        console.log("[FastUpdate] Search returned", (data as any[])?.length ?? 0, "results");

        const flights: Flight[] = ((data as any[]) ?? []).map((row: any) => ({
          id: row.id,
          flight_number: row.flight_number,
          scheduled_departure: row.dep_datetime,
          scheduled_arrival: row.arr_datetime,
          status: row.status as FlightStatus,
          status_reason: row.status_reason,
          gate: row.gate,
          created_at: row.created_at,
          updated_at: row.updated_at,
          created_by: row.created_by ?? "",
          updated_by: row.updated_by ?? "",
          organization_id: row.organization_id,
          departure_city_id: row.departure_city_id,
          arrival_city_id: row.arrival_city_id,
          aircraft_type_id: row.aircraft_type_id ?? "",
          organization: { id: row.organization_id, name: row.organization_name ?? "", code: row.organization_code ?? "" },
          departure_city: { id: row.departure_city_id, name: row.departure_city_name ?? "", code: row.departure_city_code ?? "" },
          arrival_city: { id: row.arrival_city_id, name: row.arrival_city_name ?? "", code: row.arrival_city_code ?? "" },
          aircraft_type: { id: row.aircraft_type_id ?? "", name: row.aircraft_type_name ?? "", code: row.aircraft_type_code ?? "" },
        }));
        setResults(flights);
        setShowDropdown(true);
      } catch (err: any) {
        console.error("Search error:", err);
        setSearchError(`⚠️ Search error: ${err.message || "Unknown error"}`);
        setResults([]);
        setShowDropdown(false);
      } finally {
        setSearching(false);
      }
    },
    [isOrgUser, profile?.organization_id, ensureSession]
  );

  const handleInputChange = (val: string) => {
    console.log("[FastUpdate] Input changed:", val);
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchFlights(val), 250);
  };

  const handleSelect = (flight: Flight) => {
    setSelectedFlight(flight);
    setSelectedStatus(flight.status);
    setStatusReason("");
    setShowDropdown(false);
    setQuery(flight.flight_number);
  };

  const handleUpdate = async () => {
    if (!selectedFlight || !selectedStatus || !session?.user?.id) return;

    setUpdating(true);
    try {
      // Ensure session is set before writes
      await ensureSession();

      // Derive dep_date in PNG time (UTC+10) from dep_datetime
      const depDt = new Date(selectedFlight.scheduled_departure);
      const pngDateStr = depDt.toLocaleDateString("en-CA", { timeZone: "Pacific/Port_Moresby" }); // YYYY-MM-DD

      // 1. Upsert to flight_status_overrides
      const { error: upsertError } = await supabase
        .from("flight_status_overrides")
        .upsert(
          {
            flight_number: selectedFlight.flight_number,
            dep_date: pngDateStr,
            status: selectedStatus,
            status_reason: statusReason || null,
            gate: selectedFlight.gate || null,
            updated_by: session.user.id,
          },
          { onConflict: "flight_number,dep_date" }
        );
      if (upsertError) throw upsertError;

      // 2. Write to flight_status_history for audit trail
      const { error: historyError } = await supabase
        .from("flight_status_history")
        .insert({
          flight_id: selectedFlight.id,
          previous_status: selectedFlight.status,
          new_status: selectedStatus,
          status_reason: statusReason || null,
          changed_by: session.user.id,
        });
      if (historyError) console.error("History insert error (non-fatal):", historyError);

      // 3. Success flash and reset
      setSuccessInfo({ number: selectedFlight.flight_number, status: selectedStatus });
      setTimeout(() => {
        setSuccessInfo(null);
        setSelectedFlight(null);
        setSelectedStatus(null);
        setStatusReason("");
        setQuery("");
        setResults([]);
      }, 2000);
    } catch (err: any) {
      console.error("Update failed:", err);
      alert("Update failed: " + (err.message || "Unknown error"));
    } finally {
      setUpdating(false);
    }
  };

  const resetSearch = () => {
    setSelectedFlight(null);
    setSelectedStatus(null);
    setStatusReason("");
    setQuery("");
    setResults([]);
  };

  // ── Voice command helpers ────────────────────────────────
  const STATUS_LABELS_LOWER = STATUS_BUTTONS.map((b) => b.label.toLowerCase());

  const parseTranscript = useCallback(
    (text: string) => {
      // Extract flight number: 2-3 uppercase letters + 2-5 digits
      const flightMatch = text.match(/[A-Z]{2,3}\d{2,5}/i);
      const flightNumber = flightMatch ? flightMatch[0].toUpperCase() : null;

      // Extract status (case-insensitive)
      let matchedStatus: FlightStatus | null = null;
      const lowerText = text.toLowerCase();
      // Check multi-word statuses first (longest match first)
      const sortedStatuses = [...STATUS_BUTTONS]
        .map((b) => b.label)
        .sort((a, b) => b.length - a.length);
      for (const status of sortedStatuses) {
        if (lowerText.includes(status.toLowerCase())) {
          matchedStatus = status;
          break;
        }
      }
      // Also check "check in open" without hyphen
      if (!matchedStatus && lowerText.includes("check in open")) {
        matchedStatus = "Check-in Open";
      }

      return { flightNumber, status: matchedStatus };
    },
    []
  );

  const handleVoiceResult = useCallback(
    async (text: string) => {
      const { flightNumber, status } = parseTranscript(text);

      if (!flightNumber && !status) {
        setTranscriptError(true);
        setTranscript(text);
        return;
      }

      setTranscriptError(false);
      setTranscript(text);

      if (flightNumber) {
        // Populate search and trigger search
        setQuery(flightNumber);
        const searchResults = await searchFlightsAndReturn(flightNumber);

        if (searchResults && searchResults.length > 0) {
          // Auto-select the first exact or best match
          const exactMatch = searchResults.find(
            (f) => f.flight_number.toUpperCase() === flightNumber
          );
          const best = exactMatch || searchResults[0];
          handleSelect(best);

          // If status was also found, pre-select it
          if (status) {
            setSelectedStatus(status);
          }
        } else {
          // Show dropdown with no results
          setResults([]);
          setShowDropdown(true);
        }
      } else if (status && selectedFlight) {
        // Only status found, and a flight is already selected
        setSelectedStatus(status);
      }
    },
    [parseTranscript, selectedFlight]
  );

  // Variant of searchFlights that returns results
  const searchFlightsAndReturn = useCallback(
    async (q: string): Promise<Flight[]> => {
      if (q.length < 1) return [];
      setSearching(true);
      setSearchError(null);
      try {
        await ensureSession();
        const now = new Date();
        const pngDate = new Date(now.toLocaleString("en-US", { timeZone: "Pacific/Port_Moresby" }));
        const startOfDay = new Date(pngDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(pngDate);
        endOfDay.setHours(23, 59, 59, 999);

        let qb = supabase
          .from("unified_flights" as any)
          .select("*")
          .gte("dep_datetime", startOfDay.toISOString())
          .lt("dep_datetime", endOfDay.toISOString())
          .ilike("flight_number", `%${q}%`)
          .order("dep_datetime", { ascending: true })
          .limit(15);

        if (isOrgUser && profile?.organization_id) {
          qb = qb.eq("organization_id", profile.organization_id);
        }

        const { data, error } = await qb;
        if (error) throw error;

        const flights: Flight[] = ((data as any[]) ?? []).map((row: any) => ({
          id: row.id,
          flight_number: row.flight_number,
          scheduled_departure: row.dep_datetime,
          scheduled_arrival: row.arr_datetime,
          status: row.status as FlightStatus,
          status_reason: row.status_reason,
          gate: row.gate,
          created_at: row.created_at,
          updated_at: row.updated_at,
          created_by: row.created_by ?? "",
          updated_by: row.updated_by ?? "",
          organization_id: row.organization_id,
          departure_city_id: row.departure_city_id,
          arrival_city_id: row.arrival_city_id,
          aircraft_type_id: row.aircraft_type_id ?? "",
          organization: { id: row.organization_id, name: row.organization_name ?? "", code: row.organization_code ?? "" },
          departure_city: { id: row.departure_city_id, name: row.departure_city_name ?? "", code: row.departure_city_code ?? "" },
          arrival_city: { id: row.arrival_city_id, name: row.arrival_city_name ?? "", code: row.arrival_city_code ?? "" },
          aircraft_type: { id: row.aircraft_type_id ?? "", name: row.aircraft_type_name ?? "", code: row.aircraft_type_code ?? "" },
        }));
        setResults(flights);
        return flights;
      } catch (err: any) {
        console.error("Search error:", err);
        setSearchError(`⚠️ Search error: ${err.message || "Unknown error"}`);
        setResults([]);
        return [];
      } finally {
        setSearching(false);
      }
    },
    [isOrgUser, profile?.organization_id, ensureSession]
  );

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        await sendAudioForTranscription(audioBlob);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(250); // collect chunks every 250ms for compatibility
      setIsRecording(true);
      setTranscript(null);
      setTranscriptError(false);
      resetInactivityTimer();
    } catch (err) {
      console.error("Mic access error:", err);
      setTranscript(null);
      setTranscriptError(true);
    }
  }, [resetInactivityTimer]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const sendAudioForTranscription = useCallback(
    async (blob: Blob) => {
      setIsTranscribing(true);
      resetInactivityTimer();
      try {
        const token = session?.access_token || storedTokens?.access_token;
        if (!token) throw new Error("No session");

        const formData = new FormData();
        formData.append("audio", blob, "recording.webm");

        const res = await fetch(
          `https://ejxokqjbhhxrwwvwkojd.supabase.co/functions/v1/transcribe-voice`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              apikey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqeG9rcWpiaGh4cnd3dndrb2pkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY1OTAxNDcsImV4cCI6MjA2MjE2NjE0N30.UN6sO9vPaAy_j3nSvbTe98Iki7s1OcZ0esgbUbt2fZs",
            },
            body: formData,
          }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Transcription failed");

        await handleVoiceResult(data.transcript);
      } catch (err: any) {
        console.error("Transcription error:", err);
        setTranscript(err.message || "Transcription failed");
        setTranscriptError(true);
      } finally {
        setIsTranscribing(false);
      }
    },
    [handleVoiceResult, resetInactivityTimer]
  );

  // ── Auth loading ───────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0f2c" }}>
        <p className="text-white text-xl">Loading...</p>
      </div>
    );
  }

  // ── Initialization error ──────────────────────────────
  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0f2c" }}>
        <div className="max-w-md text-center p-8">
          <p className="text-red-400 text-lg font-medium">{initError}</p>
          <button
            onClick={() => { setInitError(null); setAuthLoading(false); }}
            className="mt-4 px-6 py-2 rounded-lg font-bold"
            style={{ background: "#FFDE21", color: "#0a0f2c" }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Not logged in → login screen ───────────────────────
  if (!session || !profile) {
    return (
      <FastUpdateLogin
        onLogin={async (s, p) => {
          try {
            // Set session on shared client BEFORE any post-login queries
            await supabase.auth.setSession({
              access_token: s.access_token,
              refresh_token: s.refresh_token,
            });
            setSession(s);
            setStoredTokens({
              access_token: s.access_token,
              refresh_token: s.refresh_token,
            });
            setProfile(p);
          } catch (err: any) {
            console.error("Post-login session error:", err);
            setInitError(`⚠️ Failed to load Flight Update Tool: ${err.message || "Unknown error"}`);
          }
        }}
      />
    );
  }

  // ── Logged-in tool shell ───────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#0a0f2c" }}>
      {/* Inactivity warning */}
      {showWarning && (
        <div
          className="w-full text-center py-2 font-bold text-sm animate-pulse"
          style={{ background: "#F59E0B", color: "#0a0f2c" }}
        >
          ⚠️ You will be logged out in 60 seconds due to inactivity.
        </div>
      )}

      {/* Header */}
      <div
        className="w-full flex items-center justify-between px-4 md:px-6 py-3"
        style={{ backgroundColor: "#00c8d7" }}
      >
        <div className="flex items-center gap-3">
          <img src="/NAC_logo.png" alt="NAC Logo" className="h-8 w-auto" />
        </div>
        <span className="text-white font-bold text-lg md:text-xl tracking-wide">
          Flight Update Tool
        </span>
        <div className="flex items-center gap-4">
          <LiveClock />
          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all"
            style={{ background: "#0a0f2c", color: "#FFD700" }}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center px-4 py-8 max-w-3xl mx-auto w-full">
        {/* Mode toggle button */}
        <div className="w-full flex justify-center mb-6">
          <button
            onClick={() => setMode(mode === "text" ? "voice" : "text")}
            className="px-6 py-3 rounded-lg font-bold text-sm transition-all"
            style={{ background: "#0d1b4b", color: "#ffffff", border: "1px solid #1e3a6e" }}
          >
            {mode === "text" ? "Update Flight by Voice 🎤" : "Update Flight by Text ⌨️"}
          </button>
        </div>

        {/* Text mode: search input */}
        {mode === "text" && (
          <>
            <div ref={searchRef} className="w-full relative mb-8">
              <div className="flex">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onFocus={() => results.length > 0 && setShowDropdown(true)}
                  placeholder="Enter flight number..."
                  className="flex-1 px-6 py-4 text-xl font-bold rounded-l-lg border-0 outline-none"
                  style={{ background: "#0d1b4b", color: "#ffffff", caretColor: "#FFD700" }}
                />
                <button
                  onClick={() => searchFlights(query)}
                  className="px-6 rounded-r-lg flex items-center justify-center"
                  style={{ background: "#FFDE21" }}
                >
                  <Search className="h-6 w-6" style={{ color: "#0a0f2c" }} />
                </button>
              </div>

              {showDropdown && results.length > 0 && (
                <div
                  className="absolute z-50 w-full mt-1 rounded-lg overflow-hidden max-h-80 overflow-y-auto"
                  style={{ background: "#0d1b4b", border: "1px solid #1e3a6e" }}
                >
                  {results.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => handleSelect(f)}
                      className="w-full text-left px-5 py-3 hover:bg-[#162a5c] transition-colors border-b border-[#1e3a6e] last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-lg" style={{ color: "#FFD700" }}>
                            {f.flight_number}
                          </span>
                          <span className="text-white/70 ml-3 text-sm">{f.organization?.name}</span>
                        </div>
                        <span className="text-sm text-white/80">{f.status}</span>
                      </div>
                      <div className="text-white/60 text-sm mt-1">
                        {f.departure_city?.name} ({f.departure_city?.code}) → {f.arrival_city?.name} ({f.arrival_city?.code})
                        <span className="ml-3">{formatTime(f.scheduled_departure)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showDropdown && query.length > 0 && results.length === 0 && !searching && (
                <div
                  className="absolute z-50 w-full mt-1 rounded-lg px-5 py-4 text-center text-white/50"
                  style={{ background: "#0d1b4b" }}
                >
                  No flights found
                </div>
              )}

              {searchError && (
                <div className="mt-2 rounded-lg px-4 py-3 text-sm font-medium text-red-300 bg-red-900/40 border border-red-700">
                  {searchError}
                </div>
              )}
            </div>
          </>
        )}

        {/* Voice mode: microphone + transcript + instruction guide */}
        {mode === "voice" && (
          <>
            <div className="flex flex-col items-center mb-6">
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  startRecording();
                }}
                onPointerUp={(e) => {
                  e.preventDefault();
                  stopRecording();
                }}
                onPointerCancel={(e) => {
                  e.preventDefault();
                  stopRecording();
                }}
                disabled={isTranscribing}
                className="w-20 h-20 rounded-full flex items-center justify-center transition-all select-none touch-none"
                style={{
                  background: isRecording ? "#DC2626" : isTranscribing ? "#0d1b4b" : "#FFDE21",
                  border: isRecording ? "3px solid #ffffff" : "3px solid transparent",
                  animation: isRecording ? "pulse 1s infinite" : undefined,
                }}
              >
                {isTranscribing ? (
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#FFDE21" }} />
                ) : (
                  <Mic className="h-8 w-8" style={{ color: isRecording ? "#ffffff" : "#0a0f2c" }} />
                )}
              </button>
              <p className="text-white/60 text-sm mt-2">
                {isRecording
                  ? "Recording... Release to submit"
                  : isTranscribing
                  ? "Transcribing..."
                  : "Hold to speak"}
              </p>

              {/* Transcript display */}
              {transcript !== null && !isRecording && !isTranscribing && (
                <div className="mt-3 text-center max-w-md">
                  {transcriptError ? (
                    <p className="text-amber-400 text-sm">
                      ⚠️ Could not identify flight number or status. Please try again.
                    </p>
                  ) : (
                    <p className="text-white/70 text-sm italic">
                      🎤 Heard: &quot;{transcript}&quot;
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Three-step instruction guide */}
            {!selectedFlight && (
              <div className="w-full max-w-2xl mt-6 mb-2">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-3 rounded-lg px-4 py-3" style={{ background: "#0d1b4b" }}>
                    <span className="text-sm font-bold" style={{ color: "#FFDE21" }}>1</span>
                    <Search className="w-4 h-4 text-white shrink-0" />
                    <span className="text-white text-xs">Say a flight number</span>
                  </div>
                  <ChevronRight className="hidden sm:block w-4 h-4 shrink-0" style={{ color: "#FFDE21" }} />
                  <ChevronDown className="block sm:hidden w-4 h-4 shrink-0" style={{ color: "#FFDE21" }} />
                  <div className="flex items-center gap-3 rounded-lg px-4 py-3" style={{ background: "#0d1b4b" }}>
                    <span className="text-sm font-bold" style={{ color: "#FFDE21" }}>2</span>
                    <MousePointerClick className="w-4 h-4 text-white shrink-0" />
                    <span className="text-white text-xs">Select the flight from results</span>
                  </div>
                  <ChevronRight className="hidden sm:block w-4 h-4 shrink-0" style={{ color: "#FFDE21" }} />
                  <ChevronDown className="block sm:hidden w-4 h-4 shrink-0" style={{ color: "#FFDE21" }} />
                  <div className="flex items-center gap-3 rounded-lg px-4 py-3" style={{ background: "#0d1b4b" }}>
                    <span className="text-sm font-bold" style={{ color: "#FFDE21" }}>3</span>
                    <Check className="w-4 h-4 text-white shrink-0" />
                    <span className="text-white text-xs">Choose a status and confirm</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Selected flight panel */}
        {selectedFlight && (
          <div className="w-full animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="rounded-lg p-6 mb-6" style={{ background: "#0d1b4b" }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-3xl font-bold" style={{ color: "#FFD700" }}>
                  {selectedFlight.flight_number}
                </h2>
                <button onClick={resetSearch} className="text-white/50 hover:text-white text-sm underline">
                  Clear
                </button>
              </div>
              <p className="text-white/70 text-lg mb-2">{selectedFlight.organization?.name}</p>
              <p className="text-white text-lg mb-2">
                {selectedFlight.departure_city?.name} ({selectedFlight.departure_city?.code})
                <span className="mx-2 text-white/40">→</span>
                {selectedFlight.arrival_city?.name} ({selectedFlight.arrival_city?.code})
              </p>
              <div className="flex gap-8 mt-3 text-white/80 flex-wrap">
                <div>
                  <span className="text-white/50 text-sm">Departure</span>
                  <p className="font-mono text-lg">{formatTime(selectedFlight.scheduled_departure)}</p>
                </div>
                <div>
                  <span className="text-white/50 text-sm">Arrival</span>
                  <p className="font-mono text-lg">{formatTime(selectedFlight.scheduled_arrival)}</p>
                </div>
                {selectedFlight.gate && (
                  <div>
                    <span className="text-white/50 text-sm">Gate</span>
                    <p className="font-mono text-lg">{selectedFlight.gate}</p>
                  </div>
                )}
                <div>
                  <span className="text-white/50 text-sm">Current Status</span>
                  <p className="font-bold text-lg" style={{ color: "#FFD700" }}>
                    {selectedFlight.status}
                  </p>
                </div>
              </div>
            </div>

            {/* Status buttons grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
              {STATUS_BUTTONS.map((btn) => (
                <button
                  key={btn.label}
                  onClick={() => setSelectedStatus(btn.label)}
                  className="py-4 px-3 rounded-lg font-bold text-sm transition-all"
                  style={{
                    background: btn.bg,
                    color: btn.text,
                    border: selectedStatus === btn.label ? "3px solid #ffffff" : "3px solid transparent",
                    transform: selectedStatus === btn.label ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            {/* Status reason */}
            <input
              type="text"
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder="Status reason (optional, e.g. Mechanical delay)"
              className="w-full px-5 py-3 rounded-lg mb-6 text-white border-0 outline-none placeholder:text-white/40"
              style={{ background: "#0d1b4b" }}
            />

            {/* Update button */}
            <button
              onClick={handleUpdate}
              disabled={updating || !selectedStatus}
              className="w-full py-4 rounded-lg text-xl font-bold transition-all disabled:opacity-50"
              style={{ background: "#FFDE21", color: "#0a0f2c" }}
            >
              {updating ? "Updating..." : "Update Flight"}
            </button>
          </div>
        )}
      </div>

      {/* Success overlay */}
      {successInfo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-green-600/95 animate-in fade-in duration-300">
          <div className="text-center text-white">
            <p className="text-6xl mb-6">✅</p>
            <p className="text-3xl font-bold">Flight {successInfo.number}</p>
            <p className="text-2xl mt-2">
              updated to <span className="font-bold">{successInfo.status}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
